import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Avatar,
  TextField,
} from "@mui/material";
import quizData from "../config/quiz_logic_data.json";
import { logo, Roshetaa, whiteLogo } from "../Assets";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { createUser } from "../services/userService";
import SpinnerComponent from "./SpinnerComponent";

const questions = [
  {
    key: "Age",
    question: "How old are you?",
    options: ["Under 18", "19-24", "25-29", "30+"],
  },
  {
    key: "Skin Type",
    question: "What is your skin type?",
    options: ["Combination", "Dry", "Normal", "Oily"],
  },
  {
    key: "Concern",
    question: "What is your primary skin concern?",
    options: [
      "Under Eyes",
      "Acne",
      "Texture",
      "Dullness",
      "Wrinkles and Fine Lines",
    ],
  },
  {
    key: "Makeup Use",
    question: "Do you wear makeup regularly?",
    options: ["Daily", "Never", "Occasionally", "Rarely"],
  },
  {
    key: "Actives",
    question: "Are you using any skincare actives (e.g., retinol, AHA)?",
    options: ["Yes", "No", "I Don't Know"],
  },
];

export default function QuizByRules() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", mobile: "" });
  const [answers, setAnswers] = useState({});
  const [resultRoutine, setResultRoutine] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);

  const currentQuestion = questions[step - 1];

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAnswer = (option) => {
    const key = currentQuestion.key;

    if (key === "Concern") {
      const current = answers[key] || [];

      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : current.length < 3
        ? [...current, option]
        : current;

      setAnswers({ ...answers, [key]: updated });
    } else {
      setAnswers({ ...answers, [key]: option });
    }
  };

  const handleNext = async () => {
    if (step < questions.length) {
      setStep(step + 1);
    } else {
      const concernOrder = [
        "Under Eyes",
        "Acne",
        "Texture",
        "Dullness",
        "Wrinkles and Fine Lines",
      ];

      const normalizedAnswers = {
        ...answers,
        Concern: Array.isArray(answers["Concern"])
          ? [...answers["Concern"]]
              .sort((a, b) => concernOrder.indexOf(a) - concernOrder.indexOf(b))
              .join(", ")
          : answers["Concern"],
      };

      const match = quizData.find((row) =>
        questions.every((q) => row[q.key] === normalizedAnswers[q.key])
      );

      const routine = match?.Routine || "No matching routine found.";
      setResultRoutine(routine);
      setStep(step + 1);

      const user = {
        name: form.name,
        email: form.email,
        mobile: form.mobile,
        routine: routine,
        concern: normalizedAnswers["Concern"],
      };

      const response = await createUser({ user });

      if (response?.error) {
        console.error("Error saving user:", response.error);
      } else {
        console.log("User data saved successfully.");
      }
    }
  };

  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleRestart = () => {
    console.log("w", answers);

    setStep(0);
    setAnswers({});
    setForm({ name: "", email: "", mobile: "" });
    setResultRoutine("");
    setShowSpinner(false);
  };

  return (
    <>
      {step === 0 && (
        <Box
          minHeight="100vh"
          bgcolor="#4b777c"
          display="flex"
          alignItems="center"
          justifyContent="center"
          py={6}
          px={2}
        >
          <Box maxWidth={500} width={1} mx="auto" textAlign="start">
            <Avatar
              sx={{
                width: 300,
                height: 300,
                bgcolor: "#e2e0d4",
                fontSize: 52,
                fontWeight: "bold",
                color: "#4b777c",
                mb: 8,
                mx: "auto",
              }}
            >
              <img src={logo} alt="logo" />
            </Avatar>

            {["name", "mobile", "email"].map((field) => (
              <Box key={field}>
                <Typography
                  sx={{ color: "white", fontWeight: 700, fontSize: "35px" }}
                >
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                </Typography>
                <TextField
                  fullWidth
                  name={field}
                  value={form[field]}
                  onChange={handleFormChange}
                  margin="normal"
                  InputProps={{
                    sx: {
                      backgroundColor: "#FFFFFF",
                      border: "2px solid #fff",
                      borderRadius: 30,
                      mb: 3,
                      fontSize: "20px",
                    },
                  }}
                />
              </Box>
            ))}

            <Button
              fullWidth
              variant="contained"
              sx={{
                mt: 4,
                py: 1.8,
                fontSize: "18px",
                fontWeight: 500,
                borderRadius: "30px",
                textTransform: "none",
                backgroundColor: "#d89f84",
                "&:hover": { backgroundColor: "#c55c2bff" },
              }}
              onClick={() => setStep(1)}
              disabled={!form.name || !form.email}
            >
              <Typography
                sx={{ color: "white", fontWeight: 700, fontSize: "25px" }}
              >
                Start
              </Typography>
            </Button>
          </Box>
        </Box>
      )}
      {step > 0 && step <= questions.length && (
        <Box
          minHeight="100vh"
          bgcolor="#e2e0d4"
          display="flex"
          alignItems="center"
          justifyContent="center"
          py={6}
          px={2}
        >
          <Box maxWidth={500} width={1} mx="auto">
            <Box display="flex" justifyContent="start" mb={5}>
              <Button
                onClick={handlePrev}
                disabled={step === 1}
                sx={{
                  fontSize: "18px",
                  textTransform: "none",
                  color: "#3e5e5e",
                }}
              >
                <ChevronLeftIcon /> Previous
              </Button>
            </Box>

            <Box
              textAlign="center"
              mb={4}
              mt={4}
              display="flex"
              flexDirection="column"
              alignItems="center"
              bgcolor="#4b777c"
              p={4}
              borderRadius={4}
              boxShadow={4}
              position="relative"
            >
              <Avatar
                sx={{
                  width: 180,
                  height: 180,
                  bgcolor: "#fff",
                  position: "absolute",
                  top: -100,
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                <img src={whiteLogo} alt="whiteLogo" style={{ width: 160 }} />
              </Avatar>
              <Typography
                sx={{
                  my: 6,
                  fontSize: "40px",
                  fontWeight: "bold",
                  color: "white",
                }}
              >
                {currentQuestion.question}
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {currentQuestion.options.map((option) => {
                const selected = answers[currentQuestion.key];
                const isSelected = Array.isArray(selected)
                  ? selected.includes(option)
                  : selected === option;

                return (
                  <Grid item xs={12} key={option}>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        py: 2,
                        borderRadius: "30px",
                        fontSize: "35px",
                        fontWeight: 600,
                        backgroundColor: isSelected ? "#3e5e5e" : "#fff",
                        color: isSelected ? "#fff" : "#3e5e5e",
                        "&:hover": {
                          backgroundColor: "#203030ff",
                          color: "#fff",
                        },
                        border: "1px solid #ccc",
                        textTransform: "none",
                      }}
                      onClick={() => handleAnswer(option)}
                    >
                      {option}
                    </Button>
                  </Grid>
                );
              })}
            </Grid>

            <Button
              fullWidth
              variant="contained"
              sx={{
                mt: 4,
                py: 1.8,
                fontSize: "18px",
                fontWeight: 500,
                borderRadius: "30px",
                textTransform: "none",
                backgroundColor: "#c55c2bff",
                "&:hover": { backgroundColor: "#d89f84" },
              }}
              onClick={handleNext}
              disabled={
                currentQuestion.key === "Concern"
                  ? !(answers["Concern"] && answers["Concern"].length > 0)
                  : !answers[currentQuestion.key]
              }
            >
              <Typography
                sx={{
                  color: "white",
                  fontWeight: 700,
                  fontSize: "35px",
                  lineHeight: 1.2,
                  py: 1,
                }}
              >
                {step === questions.length ? "Submit" : "Next"}
              </Typography>
            </Button>
          </Box>
        </Box>
      )}

      {step > questions.length && !showSpinner && (
        <Box
          minHeight="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={2}
          sx={{
            backgroundImage: `url(${Roshetaa})`,
            backgroundSize: "contain", // or "cover" if you want to stretch it
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundColor: "#e2e0d4",
          }}
        >
          <Box
            bgcolor="rgba(75, 119, 124, 0)" // semi-transparent layer for readability
            textAlign="center"
            width="100%"
            maxWidth={600}
            color={"#000000"}
          >
            <Typography sx={{ fontSize: "45px", fontWeight: 700 }} gutterBottom>
              Your Personalized Routine:
            </Typography>
            <Typography
              sx={{
                fontSize: "40px",
                fontWeight: 400,
                whiteSpace: "pre-wrap",
                py: 5,
              }}
            >
              {resultRoutine}
            </Typography>
            <Box
              sx={{
                position: "absolute",
                bottom: 20,
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "space-between",
                px: 4,
                zIndex: 3,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  mt: 4,
                  py: 1.8,
                  fontSize: "18px",
                  fontWeight: 500,
                  borderRadius: "30px",
                  textTransform: "none",
                  maxWidth: "40%",
                  width: 1,
                  backgroundColor: "#d89f84",
                  "&:hover": { backgroundColor: "#c55c2bff" },
                }}
                onClick={handleRestart}
              >
                <Typography
                  sx={{ color: "white", fontWeight: 700, fontSize: "20px" }}
                >
                  Retake Quiz
                </Typography>
              </Button>

              <Button
                variant="contained"
                sx={{
                  mt: 4,
                  py: 1.8,
                  fontSize: "18px",
                  fontWeight: 500,
                  borderRadius: "30px",
                  textTransform: "none",
                  maxWidth: "40%",
                  width: 1,
                  backgroundColor: "#376e70",
                  "&:hover": { backgroundColor: "#254749ff" },
                }}
                onClick={() => setShowSpinner(true)}
              >
                <Typography
                  sx={{ fontSize: "20px", fontWeight: 600, color: "white" }}
                >
                  Show Spinner
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {showSpinner && (
        <Box minHeight="100vh">
          <SpinnerComponent handleRestart={handleRestart} />
        </Box>
      )}
    </>
  );
}
